// ToolSwitcher — the "REG" wordmark suffix, optionally an interactive
// dropdown to sibling Orbital tools (item 255 of docs/PRODUCT-ROADMAP.md,
// Phase C). Ported from Orbital/Lint's ToolSwitcher.tsx.
//
// Fetches GET /api/v1/tools to discover which sibling tools (Orbital/Git,
// Orbital/Lint) are enabled + have a URL. When none are configured, this
// renders a plain <span> identical to the static "REG" suffix in
// Wordmark.tsx, so the nav is visually unchanged. When >=1 sibling is ready,
// a chevron appears and clicking opens a menu-button dropdown.
//
// Item 289 Phase B — an operator-uploaded logo replaces the whole brand mark
// (see WordmarkBase), so there is no "REG" text to suffix. Previously that
// made the switcher disappear outright, silently dropping any configured
// sibling tool with it. Now, when a logo is set AND at least one tool is
// configured, the switcher survives as a standalone icon-only button next to
// the logo (no text, since there is no wordmark to attach it to). With a
// logo but no tools configured, it still renders nothing — there is nothing
// to switch to.
//
// Lives outside the home <Link> in AppShell because HTML forbids a <button>
// inside an <a>; WordmarkBase (glyph + "ORBITAL/") stays inside the link.
//
// Accessibility: WAI-ARIA menu button pattern — role="menu" + role="menuitem",
// Esc closes + returns focus to trigger, Arrow keys navigate items, Tab cycles
// within the open menu (focus trap).
//
// WHY THE MENU IS A PORTAL AND NOT `absolute` INSIDE THE TRIGGER
// --------------------------------------------------------------
// The switcher sits in AppShell's <aside>, which carries BOTH:
//   * `overflow-y-auto` — so an absolutely positioned child is CLIPPED at the
//     sidebar's box (60 units wide); the menu was cut off mid-word.
//   * `backdrop-blur-sm` — `backdrop-filter` establishes a stacking context,
//     which traps any z-index set inside it. The main column's header runs at
//     `relative z-40`, so it painted over the menu no matter how high the
//     menu's own z-index went. Raising `z-50` to `z-[999]` cannot fix this:
//     the value is only compared against siblings inside the aside.
// Both defects were reported together ("the big centre window overlaps the
// dropdown and the text is only half readable") and neither is fixable from
// inside the sidebar. Rendering into document.body with `position: fixed`
// escapes the clip and the stacking context at once.
//
// The trade-off a portal brings is that the menu no longer follows the trigger
// automatically, so its position is measured on open and re-measured on scroll
// and resize below.

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Check } from 'lucide-react';
import { useAvailableTools, type AvailableTool } from '../api/sibling-tools';
import { usePublicConfig } from '../api/publicConfig';

const SUFFIX_CLASSNAME =
  'text-fg-muted font-semibold tracking-[0.18em] text-[15px]';

export function ToolSwitcher() {
  const { data: cfg } = usePublicConfig();
  const { data: tools = [] } = useAvailableTools();

  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const hasTools = tools.length > 0;

  // Anchor the fixed-position menu under the trigger. `fixed` is relative to
  // the viewport, so the trigger's viewport rect is exactly what we need — no
  // scroll offsets to add.
  const place = useCallback(() => {
    const r = triggerRef.current?.getBoundingClientRect();
    if (r) setPos({ top: r.bottom + 6, left: r.left });
  }, []);

  // Measure before paint so the menu never shows up at 0,0 for one frame.
  useLayoutEffect(() => {
    if (open) place();
  }, [open, place]);

  // A portal does not travel with the trigger. Re-measure on scroll (capture
  // phase: the sidebar scrolls itself, and those events do not bubble to
  // window) and on resize.
  useEffect(() => {
    if (!open) return;
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [open, place]);

  useEffect(() => {
    if (!open) return;

    const firstItem = menuRef.current?.querySelector<HTMLElement>(
      '[role="menuitem"]',
    );
    firstItem?.focus();

    const getItems = (): HTMLElement[] =>
      Array.from(
        menuRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? [],
      );

    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
        return;
      }

      if (e.key === 'Tab') {
        const items = getItems();
        if (items.length === 0) return;
        const idx = items.indexOf(document.activeElement as HTMLElement);
        if (e.shiftKey) {
          e.preventDefault();
          items[(idx - 1 + items.length) % items.length].focus();
        } else {
          e.preventDefault();
          items[(idx + 1) % items.length].focus();
        }
        return;
      }

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        const items = getItems();
        const idx = items.indexOf(document.activeElement as HTMLElement);
        items[(idx + 1) % items.length]?.focus();
        return;
      }

      if (e.key === 'ArrowUp') {
        e.preventDefault();
        const items = getItems();
        const idx = items.indexOf(document.activeElement as HTMLElement);
        items[(idx - 1 + items.length) % items.length]?.focus();
      }
    };

    // Outside-click must treat the portalled menu as "inside" too — it is no
    // longer a DOM descendant of the trigger's container.
    const handleMouseDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (containerRef.current?.contains(t) || menuRef.current?.contains(t)) {
        return;
      }
      setOpen(false);
    };

    document.addEventListener('keydown', handleKey);
    document.addEventListener('mousedown', handleMouseDown);
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.removeEventListener('mousedown', handleMouseDown);
    };
  }, [open]);

  const hasCustomLogo = Boolean(cfg?.logo_url);

  // A customer logo already replaces the whole brand mark (see
  // WordmarkBase) — there is no separate "REG" suffix to render. Without any
  // sibling tool configured there is nothing to switch to either, so render
  // nothing, same as the no-tools case below.
  if (hasCustomLogo && !hasTools) {
    return null;
  }

  if (!hasCustomLogo && !hasTools) {
    return (
      <span data-tool-switch className={SUFFIX_CLASSNAME}>
        REG
      </span>
    );
  }

  return (
    <div ref={containerRef} className="relative">
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Switch tool"
        onClick={() => setOpen((o) => !o)}
        className={
          hasCustomLogo
            ? 'inline-flex items-center justify-center rounded p-1.5 text-fg-muted hover:text-fg hover:bg-bg-hover transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-accent/60'
            : `inline-flex items-center gap-px rounded hover:text-fg transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-accent/60 ${SUFFIX_CLASSNAME}`
        }
      >
        {!hasCustomLogo && <span data-tool-switch>REG</span>}
        <ChevronDown
          className={`h-3 w-3 transition-transform duration-150 ${open ? 'rotate-180' : ''}`}
          aria-hidden="true"
        />
      </button>

      {open && createPortal(
        <div
          ref={menuRef}
          role="menu"
          aria-label="Orbital tools"
          // Deliberately NOT gated on `pos` being set: the menu must exist
          // whenever `open` is true, or a missed layout effect would silently
          // swallow it. useLayoutEffect fills `pos` before the browser paints,
          // so the 0,0 fallback is never actually visible.
          style={{ position: 'fixed', top: pos?.top ?? 0, left: pos?.left ?? 0 }}
          // z-[60] beats the main header's z-40. Because this now lives on
          // document.body, that comparison is finally meaningful — inside the
          // backdrop-blurred aside it was not.
          className="min-w-[11rem] rounded-md border border-border bg-bg-raised shadow-lg py-1 z-[60]"
        >
          {/* Current tool — shown as selected, not interactive.
              Spelled "Orbital/Reg" to match the sibling labels the backend
              produces ("Orbital/Git", "Orbital/Lint" — see
              api/internal/tools/sibling.go) and the wordmark itself
              ("ORBITAL/REG"). It read "OrbitalReg" here, which made the current
              entry the only one in the menu without the slash. */}
          <div
            role="menuitem"
            aria-current="true"
            tabIndex={0}
            className="flex items-center gap-2 px-3 py-2 text-[11px] font-semibold uppercase tracking-[0.08em] text-accent bg-accent/10 cursor-default select-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent/60"
          >
            <Check className="h-3 w-3 shrink-0" aria-hidden="true" />
            Orbital/Reg
          </div>

          <hr className="my-0.5 border-border" />

          {tools.map((tool: AvailableTool) => (
            <a
              key={tool.key}
              href={tool.base_url}
              role="menuitem"
              tabIndex={0}
              className="flex items-center gap-2 px-3 py-2 text-[11px] font-semibold uppercase tracking-[0.08em] text-fg-muted hover:text-fg hover:bg-bg-hover transition-colors select-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent/60"
              onClick={() => setOpen(false)}
            >
              <span className="h-3 w-3 shrink-0" aria-hidden="true" />
              {tool.label}
            </a>
          ))}
        </div>,
        document.body,
      )}
    </div>
  );
}
